// src/features/coupon/components/CouponCreateModal.tsx

import React, {
  useMemo,
  useState,
} from 'react';

import {
  Plus,
  Trash2,
  X,
} from 'lucide-react';

import type {
  Coupon,
  CouponCreateRequest,
} from '../types/coupon.admin.types';

import type {
  CouponRewardMode,
  CouponRewardType,
} from '../types/coupon.types';

import {
  adminCouponService,
} from '../services/adminCouponService';


interface StoreOption {
  storeId: number;
  storeName: string;
}


interface CouponCreateModalProps {
  stores: StoreOption[];

  currentRole:
    'ADMIN' | 'SUPERADMIN';

  currentStoreId:
    number | null;

  onClose: () => void;

  onCreated: (
    coupon: Coupon,
  ) => void;
}


/**
 * =========================================================
 * RANDOM 쿠폰 생성 화면용 타입
 *
 * API의 probabilityWeight와 별개로
 * 화면에서는 실제 % 문자열로 관리합니다.
 *
 * 예:
 *
 * "33.33" %
 * -> 서버 3333
 * =========================================================
 */
interface RandomRewardOptionForm {
  rewardType:
    CouponRewardType;

  rewardAmount:
    number;

  storeId:
    number;

  probabilityPercent:
    string;
}


const REWARD_TYPES: {
  value: CouponRewardType;
  label: string;
}[] = [
  {
    value: 'POINT',
    label: '라포이트 시드',
  },
  {
    value: 'STORE_POINT',
    label: '매장 포인트',
  },
  {
    value: 'FREE_GAME',
    label: '무료 게임',
  },
  {
    value: 'SATELLITE',
    label: '새틀라이트',
  },
  {
    value: 'SEAT',
    label: '시트권',
  },
  {
    value: 'ETC',
    label: '기타',
  },
];


const RANDOM_REWARD_TYPES: {
  value: CouponRewardType;
  label: string;
}[] = [
  {
    value: 'POINT',
    label: '라포이트 시드',
  },
  {
    value: 'STORE_POINT',
    label: '매장 포인트',
  },
];


const createEmptyRandomOption = (
  storeId: number,
): RandomRewardOptionForm => ({
  rewardType:
    'STORE_POINT',

  rewardAmount:
    1000,

  storeId,

  probabilityPercent:
    '',
});


/**
 * =========================================================
 * 확률 입력 정규화
 *
 * 허용:
 * 1
 * 1.
 * 1.2
 * 1.25
 * 0.01
 *
 * 소수점 최대 2자리
 * =========================================================
 */
const normalizeProbabilityInput = (
  value: string,
): string => {

  let normalized =
    value.replace(
      /[^0-9.]/g,
      '',
    );


  /**
   * 소수점 하나만 허용
   */
  const firstDotIndex =
    normalized.indexOf(
      '.',
    );


  if (
    firstDotIndex !==
    -1
  ) {

    normalized =
      normalized.slice(
        0,
        firstDotIndex +
          1,
      ) +
      normalized
        .slice(
          firstDotIndex +
            1,
        )
        .replace(
          /\./g,
          '',
        );
  }


  const [
    integerPart = '',
    decimalPart = '',
  ] =
    normalized.split(
      '.',
    );


  /**
   * 정수부 앞자리 0 정리
   *
   * 0005 -> 5
   *
   * 단,
   * 0.5는 그대로 유지
   */
  let normalizedInteger =
    integerPart.replace(
      /^0+(?=\d)/,
      '',
    );


  if (
    normalizedInteger ===
      '' &&
    normalized.startsWith(
      '.',
    )
  ) {
    normalizedInteger =
      '0';
  }


  /**
   * 소수점이 존재했다면
   * 소수점 형태 유지
   */
  if (
    normalized.includes(
      '.',
    )
  ) {

    return `${normalizedInteger}.${decimalPart.slice(
      0,
      2,
    )}`;
  }


  return normalizedInteger;
};


/**
 * =========================================================
 * 화면 % -> 서버 probabilityWeight
 *
 * 100.00% = 10000
 * 33.33%  = 3333
 * 0.01%   = 1
 * =========================================================
 */
const percentToWeight = (
  value: string,
): number => {

  const percent =
    Number(
      value,
    );


  if (
    !Number.isFinite(
      percent,
    )
  ) {
    return 0;
  }


  return Math.round(
    percent *
    100,
  );
};


export default function CouponCreateModal({
  stores,
  currentRole,
  currentStoreId,
  onClose,
  onCreated,
}: CouponCreateModalProps) {

  const isSuperAdmin =
    currentRole ===
    'SUPERADMIN';


  const initialStoreId =
    isSuperAdmin
      ? (
          stores[0]?.storeId ??
          0
        )
      : (
          currentStoreId ??
          0
        );


  const [
    couponName,
    setCouponName,
  ] =
    useState('');


  const [
    description,
    setDescription,
  ] =
    useState('');


  const [
    rewardMode,
    setRewardMode,
  ] =
    useState<CouponRewardMode>(
      'FIXED',
    );


  const [
    rewardType,
    setRewardType,
  ] =
    useState<CouponRewardType>(
      'STORE_POINT',
    );


  const [
    rewardAmount,
    setRewardAmount,
  ] =
    useState(1000);


  const [
    storeId,
    setStoreId,
  ] =
    useState(
      initialStoreId,
    );


  const [
    validDays,
    setValidDays,
  ] =
    useState(30);


  const [
    rewardOptions,
    setRewardOptions,
  ] =
    useState<
      RandomRewardOptionForm[]
    >([
      createEmptyRandomOption(
        initialStoreId,
      ),
    ]);


  const [
    submitting,
    setSubmitting,
  ] =
    useState(false);


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState('');


  /**
   * =========================================================
   * RANDOM 확률 합계
   *
   * 서버 단위 기준
   *
   * 10000 = 100.00%
   * =========================================================
   */
  const totalProbabilityWeight =
    useMemo(
      () =>
        rewardOptions.reduce(
          (
            sum,
            option,
          ) =>
            sum +
            percentToWeight(
              option
                .probabilityPercent,
            ),
          0,
        ),
      [
        rewardOptions,
      ],
    );


  const totalProbabilityPercent =
    totalProbabilityWeight /
    100;


  const isProbabilityComplete =
    totalProbabilityWeight ===
    10000;


  /**
   * =========================================================
   * 매장 변경
   * =========================================================
   */
  const handleStoreChange = (
    nextStoreId: number,
  ): void => {

    setStoreId(
      nextStoreId,
    );


    setRewardOptions(
      (previous) =>
        previous.map(
          (option) => ({
            ...option,

            storeId:
              nextStoreId,
          }),
        ),
    );
  };


  /**
   * =========================================================
   * RANDOM 옵션 추가
   * =========================================================
   */
  const handleAddOption =
    (): void => {

      setRewardOptions(
        (previous) => [
          ...previous,

          createEmptyRandomOption(
            storeId,
          ),
        ],
      );
    };


  /**
   * =========================================================
   * RANDOM 옵션 일반 수정
   * =========================================================
   */
  const handleUpdateOption = <
    K extends
      keyof RandomRewardOptionForm,
  >(
    index: number,
    key: K,
    value:
      RandomRewardOptionForm[K],
  ): void => {

    setRewardOptions(
      (previous) =>
        previous.map(
          (
            option,
            optionIndex,
          ) =>
            optionIndex ===
            index
              ? {
                  ...option,

                  [key]:
                    value,
                }
              : option,
        ),
    );
  };


  /**
   * =========================================================
   * RANDOM 확률 수정
   *
   * 화면에서는 % 단위
   * 소수점 2자리까지
   * =========================================================
   */
  const handleProbabilityChange = (
    index: number,
    value: string,
  ): void => {

    const normalized =
      normalizeProbabilityInput(
        value,
      );


    /**
     * 비어있는 입력은 허용
     */
    if (
      normalized ===
      ''
    ) {

      handleUpdateOption(
        index,
        'probabilityPercent',
        '',
      );

      return;
    }


    const probability =
      Number(
        normalized,
      );


    /**
     * 입력 중 "1." 같은 형태
     */
    if (
      normalized.endsWith(
        '.',
      )
    ) {

      if (
        probability >
        100
      ) {
        return;
      }


      handleUpdateOption(
        index,
        'probabilityPercent',
        normalized,
      );

      return;
    }


    if (
      !Number.isFinite(
        probability,
      ) ||
      probability >
        100
    ) {
      return;
    }


    handleUpdateOption(
      index,
      'probabilityPercent',
      normalized,
    );
  };


  /**
   * =========================================================
   * RANDOM 옵션 삭제
   * =========================================================
   */
  const handleRemoveOption = (
    index: number,
  ): void => {

    setRewardOptions(
      (previous) =>
        previous.filter(
          (
            _,
            optionIndex,
          ) =>
            optionIndex !==
            index,
        ),
    );
  };


  /**
   * =========================================================
   * Submit 검증
   * =========================================================
   */
  const validateForm =
    (): string | null => {

      if (
        !couponName.trim()
      ) {
        return '쿠폰명을 입력해주세요.';
      }


      if (
        storeId <=
        0
      ) {
        return '매장을 선택해주세요.';
      }


      if (
        validDays <=
        0
      ) {
        return '유효기간을 입력해주세요.';
      }


      if (
        rewardMode ===
        'FIXED'
      ) {

        if (
          rewardAmount <
          0
        ) {
          return '고정 보상 금액은 0 이상이어야 합니다.';
        }


        return null;
      }


      if (
        rewardOptions.length ===
        0
      ) {
        return '확률 보상을 하나 이상 추가해주세요.';
      }


      for (
        const option of
        rewardOptions
      ) {

        if (
          option.rewardType !==
            'POINT' &&
          option.rewardType !==
            'STORE_POINT'
        ) {

          return '확률 쿠폰에는 포인트 또는 매장포인트만 사용할 수 있습니다.';
        }


        if (
          option.rewardAmount <
          0
        ) {

          return '확률 보상 금액은 0이상이어야 합니다.';
        }


        const probabilityWeight =
          percentToWeight(
            option
              .probabilityPercent,
          );


        if (
          probabilityWeight <=
          0
        ) {

          return '각 보상의 확률은 0%보다 커야 합니다.';
        }


        if (
          probabilityWeight >
          10000
        ) {

          return '각 보상의 확률은 100%를 초과할 수 없습니다.';
        }
      }


      if (
        !isProbabilityComplete
      ) {

        return `확률 합계는 반드시 100.00%여야 합니다. 현재 ${totalProbabilityPercent.toFixed(
          2,
        )}%입니다.`;
      }


      return null;
    };


  /**
   * =========================================================
   * 쿠폰 생성
   * =========================================================
   */
  const handleSubmit =
    async (): Promise<void> => {

      if (
        submitting
      ) {
        return;
      }


      const validationError =
        validateForm();


      if (
        validationError
      ) {

        setErrorMessage(
          validationError,
        );

        return;
      }


      try {

        setSubmitting(
          true,
        );


        setErrorMessage(
          '',
        );


        const request:
          CouponCreateRequest =
          rewardMode ===
          'FIXED'
            ? {
                couponName:
                  couponName.trim(),

                description:
                  description.trim() ||
                  null,

                rewardMode:
                  'FIXED',

                rewardType,

                rewardAmount,

                storeId,

                validDays,

                rewardOptions:
                  [],
              }
            : {
                couponName:
                  couponName.trim(),

                description:
                  description.trim() ||
                  null,

                rewardMode:
                  'RANDOM',

                rewardType:
                  null,

                rewardAmount:
                  null,

                storeId,

                validDays,

                rewardOptions:
                  rewardOptions.map(
                    (option) => ({
                      rewardType:
                        option.rewardType,

                      rewardAmount:
                        option.rewardAmount,

                      storeId,

                      probabilityWeight:
                        percentToWeight(
                          option
                            .probabilityPercent,
                        ),
                    }),
                  ),
              };


        const created =
          await adminCouponService
            .createCoupon(
              request,
            );


        onCreated(
          created,
        );


        onClose();

      } catch (
        error
      ) {

        console.error(
          '쿠폰 생성 실패:',
          error,
        );


        setErrorMessage(
          '쿠폰 생성에 실패했습니다.',
        );

      } finally {

        setSubmitting(
          false,
        );

      }
    };


  return (
    <div
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-black/50
        px-4
        py-6
      "
      onClick={
        onClose
      }
    >

      <div
        role="dialog"
        aria-modal="true"
        onClick={(
          event,
        ) =>
          event.stopPropagation()
        }
        className="
          flex
          max-h-[90vh]
          w-full
          max-w-2xl
          flex-col
          overflow-hidden
          rounded-2xl
          bg-white
          shadow-2xl
        "
      >

        {/* Header */}
        <div
          className="
            flex
            shrink-0
            items-center
            justify-between
            border-b
            border-gray-200
            px-5
            py-4
          "
        >

          <div>

            <h2
              className="
                text-lg
                font-bold
                text-gray-900
              "
            >
              쿠폰 생성
            </h2>


            <p
              className="
                mt-0.5
                text-xs
                text-gray-500
              "
            >
              고정 보상 또는 확률 보상 쿠폰을 생성합니다.
            </p>

          </div>


          <button
            type="button"
            onClick={
              onClose
            }
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-full
              text-gray-400
              transition
              hover:bg-gray-100
              hover:text-gray-700
            "
          >
            <X
              size={18}
            />
          </button>

        </div>


        {/* Body */}
        <div
          className="
            min-h-0
            flex-1
            space-y-5
            overflow-y-auto
            px-5
            py-5
          "
        >

          {/* 쿠폰명 */}
          <div>

            <label
              className="
                text-sm
                font-semibold
                text-gray-700
              "
            >
              쿠폰명
            </label>


            <input
              type="text"
              value={
                couponName
              }
              onChange={(
                event,
              ) =>
                setCouponName(
                  event.target.value,
                )
              }
              placeholder="예: 신규회원 1만P"
              className="
                mt-1.5
                h-11
                w-full
                rounded-lg
                border
                border-gray-300
                px-3
                text-sm
                outline-none
                transition
                focus:border-main
                focus:ring-1
                focus:ring-main
              "
            />

          </div>


          {/* 설명 */}
          <div>

            <label
              className="
                text-sm
                font-semibold
                text-gray-700
              "
            >
              설명
            </label>


            <textarea
              value={
                description
              }
              onChange={(
                event,
              ) =>
                setDescription(
                  event.target.value,
                )
              }
              rows={3}
              placeholder="쿠폰 설명을 입력해주세요."
              className="
                mt-1.5
                w-full
                resize-none
                rounded-lg
                border
                border-gray-300
                px-3
                py-2.5
                text-sm
                outline-none
                transition
                focus:border-main
                focus:ring-1
                focus:ring-main
              "
            />

          </div>


          {/* 매장 / 유효기간 */}
          <div
            className="
              grid
              grid-cols-1
              gap-4
              sm:grid-cols-2
            "
          >

            <div>

              <label
                className="
                  text-sm
                  font-semibold
                  text-gray-700
                "
              >
                사용 매장
              </label>


              {isSuperAdmin ? (

                <select
                  value={
                    storeId
                  }
                  onChange={(
                    event,
                  ) =>
                    handleStoreChange(
                      Number(
                        event.target.value,
                      ),
                    )
                  }
                  className="
                    mt-1.5
                    h-11
                    w-full
                    rounded-lg
                    border
                    border-gray-300
                    bg-white
                    px-3
                    text-sm
                    outline-none
                    focus:border-main
                  "
                >

                  {stores.map(
                    (store) => (
                      <option
                        key={
                          store.storeId
                        }
                        value={
                          store.storeId
                        }
                      >
                        {store.storeName}
                      </option>
                    ),
                  )}

                </select>

              ) : (

                <div
                  className="
                    mt-1.5
                    flex
                    h-11
                    items-center
                    rounded-lg
                    border
                    border-gray-200
                    bg-gray-50
                    px-3
                    text-sm
                    text-gray-700
                  "
                >
                  {
                    stores.find(
                      (store) =>
                        store.storeId ===
                        currentStoreId,
                    )
                      ?.storeName ??
                    '매장 정보 없음'
                  }
                </div>

              )}

            </div>


            <div>

              <label
                className="
                  text-sm
                  font-semibold
                  text-gray-700
                "
              >
                유효기간
              </label>


              <div
                className="
                  mt-1.5
                  flex
                  items-center
                  gap-2
                "
              >

                <input
                  type="number"
                  min={1}
                  value={
                    validDays
                  }
                  onChange={(
                    event,
                  ) =>
                    setValidDays(
                      Number(
                        event.target.value,
                      ),
                    )
                  }
                  className="
                    h-11
                    min-w-0
                    flex-1
                    rounded-lg
                    border
                    border-gray-300
                    px-3
                    text-sm
                    outline-none
                    focus:border-main
                  "
                />

                <span
                  className="
                    shrink-0
                    text-sm
                    text-gray-500
                  "
                >
                  일
                </span>

              </div>

            </div>

          </div>


          {/* 보상 방식 */}
          <div>

            <label
              className="
                text-sm
                font-semibold
                text-gray-700
              "
            >
              보상 방식
            </label>


            <div
              className="
                mt-2
                grid
                grid-cols-2
                gap-2
              "
            >

              <button
                type="button"
                onClick={() =>
                  setRewardMode(
                    'FIXED',
                  )
                }
                className={`
                  h-10
                  rounded-lg
                  border
                  text-sm
                  font-bold
                  transition

                  ${
                    rewardMode ===
                    'FIXED'
                      ? `
                        border-main
                        bg-main/5
                        text-main
                      `
                      : `
                        border-gray-200
                        bg-white
                        text-gray-500
                      `
                  }
                `}
              >
                고정 보상
              </button>


              <button
                type="button"
                onClick={() =>
                  setRewardMode(
                    'RANDOM',
                  )
                }
                className={`
                  h-10
                  rounded-lg
                  border
                  text-sm
                  font-bold
                  transition

                  ${
                    rewardMode ===
                    'RANDOM'
                      ? `
                        border-main
                        bg-main/5
                        text-main
                      `
                      : `
                        border-gray-200
                        bg-white
                        text-gray-500
                      `
                  }
                `}
              >
                확률 보상
              </button>

            </div>

          </div>


          {/* FIXED */}
          {rewardMode ===
            'FIXED' && (

            <div
              className="
                rounded-xl
                border
                border-gray-200
                bg-gray-50/50
                p-4
              "
            >

              <p
                className="
                  mb-3
                  text-sm
                  font-bold
                  text-gray-800
                "
              >
                고정 보상 설정
              </p>


              <div
                className="
                  grid
                  grid-cols-1
                  gap-3
                  sm:grid-cols-2
                "
              >

                <div>

                  <label
                    className="
                      text-xs
                      font-semibold
                      text-gray-600
                    "
                  >
                    보상 종류
                  </label>


                  <select
                    value={
                      rewardType
                    }
                    onChange={(
                      event,
                    ) =>
                      setRewardType(
                        event.target
                          .value as
                          CouponRewardType,
                      )
                    }
                    className="
                      mt-1
                      h-10
                      w-full
                      rounded-lg
                      border
                      border-gray-300
                      bg-white
                      px-3
                      text-sm
                    "
                  >

                    {REWARD_TYPES.map(
                      (item) => (
                        <option
                          key={
                            item.value
                          }
                          value={
                            item.value
                          }
                        >
                          {item.label}
                        </option>
                      ),
                    )}

                  </select>

                </div>


                <div>

                  <label
                    className="
                      text-xs
                      font-semibold
                      text-gray-600
                    "
                  >
                    수량 / 금액
                  </label>


                  <input
                    type="number"
                    min={0}
                    value={
                      rewardAmount
                    }
                    onChange={(
                      event,
                    ) =>
                      setRewardAmount(
                        Number(
                          event.target.value,
                        ),
                      )
                    }
                    className="
                      mt-1
                      h-10
                      w-full
                      rounded-lg
                      border
                      border-gray-300
                      bg-white
                      px-3
                      text-sm
                    "
                  />

                </div>

              </div>

            </div>

          )}


          {/* RANDOM */}
          {rewardMode ===
            'RANDOM' && (

            <div
              className="
                rounded-xl
                border
                border-gray-200
                bg-gray-50/50
                p-4
              "
            >

              <div
                className="
                  flex
                  items-center
                  justify-between
                  gap-2
                "
              >

                <div>

                  <p
                    className="
                      text-sm
                      font-bold
                      text-gray-800
                    "
                  >
                    확률 보상 설정
                  </p>


                  <p
                    className="
                      mt-0.5
                      text-[11px]
                      text-gray-500
                    "
                  >
                    POINT / STORE_POINT만 등록할 수 있습니다.
                  </p>

                </div>


                <button
                  type="button"
                  onClick={
                    handleAddOption
                  }
                  className="
                    inline-flex
                    h-8
                    items-center
                    gap-1
                    rounded-lg
                    border
                    border-main
                    bg-white
                    px-2.5
                    text-xs
                    font-bold
                    text-main
                  "
                >
                  <Plus
                    size={14}
                  />

                  옵션 추가
                </button>

              </div>


              <div
                className="
                  mt-4
                  space-y-3
                "
              >

                {rewardOptions.map(
                  (
                    option,
                    index,
                  ) => (

                    <div
                      key={
                        index
                      }
                      className="
                        rounded-lg
                        border
                        border-gray-200
                        bg-white
                        p-3
                      "
                    >

                      <div
                        className="
                          mb-2
                          flex
                          items-center
                          justify-between
                        "
                      >

                        <span
                          className="
                            text-xs
                            font-bold
                            text-gray-700
                          "
                        >
                          보상 #{index + 1}
                        </span>


                        {rewardOptions.length >
                          1 && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveOption(
                                index,
                              )
                            }
                            className="
                              flex
                              h-7
                              w-7
                              items-center
                              justify-center
                              rounded-md
                              text-red-400
                              hover:bg-red-50
                            "
                          >
                            <Trash2
                              size={14}
                            />
                          </button>
                        )}

                      </div>


                      <div
                        className="
                          grid
                          grid-cols-1
                          gap-2
                          sm:grid-cols-3
                        "
                      >

                        <select
                          value={
                            option
                              .rewardType
                          }
                          onChange={(
                            event,
                          ) =>
                            handleUpdateOption(
                              index,
                              'rewardType',
                              event.target
                                .value as
                                CouponRewardType,
                            )
                          }
                          className="
                            h-10
                            rounded-lg
                            border
                            border-gray-300
                            bg-white
                            px-2
                            text-xs
                          "
                        >

                          {RANDOM_REWARD_TYPES.map(
                            (item) => (
                              <option
                                key={
                                  item.value
                                }
                                value={
                                  item.value
                                }
                              >
                                {item.label}
                              </option>
                            ),
                          )}

                        </select>


                        <input
                          type="number"
                          min={0}
                          value={
                            option
                              .rewardAmount
                          }
                          onChange={(
                            event,
                          ) =>
                            handleUpdateOption(
                              index,
                              'rewardAmount',
                              Number(
                                event.target.value,
                              ),
                            )
                          }
                          placeholder="보상 금액"
                          className="
                            h-10
                            rounded-lg
                            border
                            border-gray-300
                            px-2
                            text-xs
                          "
                        />


                        {/*
                         * =========================================
                         * 확률 %
                         *
                         * 사용자는 실제 % 입력
                         *
                         * 33.33 -> 서버 3333
                         * =========================================
                         */}
                        <div
                          className="
                            relative
                          "
                        >

                          <input
                            type="text"
                            inputMode="decimal"
                            value={
                              option
                                .probabilityPercent
                            }
                            onChange={(
                              event,
                            ) =>
                              handleProbabilityChange(
                                index,
                                event.target.value,
                              )
                            }
                            placeholder="0.00"
                            className="
                              h-10
                              w-full
                              rounded-lg
                              border
                              border-gray-300
                              px-2
                              pr-8
                              text-right
                              text-xs
                              font-semibold
                              outline-none
                              transition
                              focus:border-main
                              focus:ring-1
                              focus:ring-main
                            "
                          />


                          <span
                            className="
                              pointer-events-none
                              absolute
                              right-3
                              top-1/2
                              -translate-y-1/2
                              text-xs
                              font-semibold
                              text-gray-400
                            "
                          >
                            %
                          </span>

                        </div>

                      </div>


                      <p
                        className="
                          mt-1.5
                          text-right
                          text-[10px]
                          text-gray-400
                        "
                      >
                        서버 가중치:{' '}
                        {percentToWeight(
                          option
                            .probabilityPercent,
                        )}
                        /10000
                      </p>

                    </div>

                  ),
                )}

              </div>


              {/* 확률 합계 */}
              <div
                className={`
                  mt-4
                  flex
                  items-center
                  justify-between
                  rounded-lg
                  px-3
                  py-2.5
                  text-sm
                  font-bold

                  ${
                    isProbabilityComplete
                      ? `
                        bg-emerald-50
                        text-emerald-700
                      `
                      : `
                        bg-red-50
                        text-red-600
                      `
                  }
                `}
              >

                <span>
                  확률 합계
                </span>


                <span>
                  {totalProbabilityPercent.toFixed(
                    2,
                  )}
                  %
                </span>

              </div>


              <p
                className="
                  mt-2
                  text-right
                  text-[11px]
                  text-gray-400
                "
              >
                확률의 합계가 100.00%가 되어야 생성할 수 있습니다.
              </p>

            </div>

          )}


          {errorMessage && (
            <div
              className="
                rounded-lg
                bg-red-50
                px-3
                py-2.5
                text-sm
                text-red-600
              "
            >
              {errorMessage}
            </div>
          )}

        </div>


        {/* Footer */}
        <div
          className="
            flex
            shrink-0
            justify-end
            gap-2
            border-t
            border-gray-200
            bg-gray-50
            px-5
            py-3
          "
        >

          <button
            type="button"
            disabled={
              submitting
            }
            onClick={
              onClose
            }
            className="
              h-10
              rounded-lg
              border
              border-gray-300
              bg-white
              px-4
              text-sm
              font-semibold
              text-gray-600
            "
          >
            취소
          </button>


          <button
            type="button"
            disabled={
              submitting
            }
            onClick={() =>
              void handleSubmit()
            }
            className="
              h-10
              rounded-lg
              bg-main
              px-5
              text-sm
              font-bold
              text-white
              transition
              hover:opacity-90
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            {submitting
              ? '생성 중...'
              : '쿠폰 생성'}
          </button>

        </div>

      </div>

    </div>
  );
}